import React, { useContext } from 'react';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EllipsisOutlined,
  EyeOutlined,
  ReconciliationOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, MenuProps } from 'antd';

import IBTooltip from '@totem/components/common/helpTooltips/IBTooltip';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { Event } from '@totem/types/events';
import { SecurityGroupCheckConstraints } from '@totem/types/user';

type Props = {
  event: Event;
  onView: (event: Event) => void;
  onAcknowledge: (event: Event) => void;
  onClear: (event: Event) => void;
  onCreateTicket: (event: Event) => void;
  onShowAssociateTicket: (event: Event) => void;
};

const EventActionMenu = (props: Props) => {
  const { userProfile } = useContext(UserProfileContext);
  const eligibleToAcknowledge =
    props.event.status !== 'ACKNOWLEDGED' && props.event.status !== 'CLEARED';

  const eligibleToClear = props.event.status !== 'CLEARED';

  const securityConstraints: SecurityGroupCheckConstraints = {
    regionId: props.event.regionId,
    buildingId: props.event.buildingId,
    controlSystemId: props.event.controlSystemId,
  };

  const eligibleForTicketCreation = () => {
    if (typeof props.event.keys !== 'undefined' && props.event.keys !== null) {
      for (let idx = 0; idx < props.event.keys.length; idx++) {
        if (
          props.event.keys[idx].system === 'Ticketing' &&
          props.event.keys[idx].instance === 'IBD'
        ) {
          return false;
        }
      }
    }
    return true;
  };

  const getActionMenuItems = (): MenuProps['items'] => {
    const menuItems: MenuProps['items'] = [
      {
        key: 'event_details',
        label: 'Event Details',
        title: 'Event Details',
        icon: <EyeOutlined />,
      },
    ];

    if (
      eligibleToAcknowledge &&
      permissions.eventManager.hasPermissions(userProfile, securityConstraints)
    ) {
      menuItems.push({
        key: 'acknowledge_event',
        label: (
          <IBTooltip content={permissions.eventManager.tooltip}>
            Acknowledge Event
          </IBTooltip>
        ),
        title: 'Acknowledge Event',
        icon: <CheckCircleOutlined />,
      });
    }

    if (
      eligibleToClear &&
      permissions.eventManager.hasPermissions(userProfile, securityConstraints)
    ) {
      menuItems.push({
        key: 'clear_event',
        label: (
          <IBTooltip content={permissions.eventManager.tooltip}>
            Clear Event
          </IBTooltip>
        ),
        title: 'Clear Event',
        icon: <CloseCircleOutlined />,
      });
    }

    if (
      permissions.ticketingCreator.hasPermissions(
        userProfile,
        securityConstraints,
      ) &&
      eligibleForTicketCreation()
    ) {
      menuItems.push({
        key: 'create_ticket',
        label: (
          <IBTooltip content={permissions.ticketingCreator.tooltip}>
            Create Ticket
          </IBTooltip>
        ),
        title: 'Create Ticket',
        icon: <ReconciliationOutlined />,
      });

      menuItems.push({
        key: 'associate_ticket',
        label: 'Associate Ticket',
        title: 'Associate Ticket',
        icon: <ReconciliationOutlined />,
      });
    }

    return menuItems;
  };

  const onMenuClicked = ({ key }) => {
    // eslint-disable-next-line default-case
    switch (key) {
      case 'event_details':
        props.onView(props.event);
        break;
      case 'acknowledge_event':
        props.onAcknowledge(props.event);
        break;
      case 'clear_event':
        props.onClear(props.event);
        break;
      case 'create_ticket':
        props.onCreateTicket(props.event);
        break;
      case 'associate_ticket':
        props.onShowAssociateTicket(props.event);
        break;
    }
  };

  return (
    <Dropdown
      menu={{ items: getActionMenuItems(), onClick: onMenuClicked }}
      trigger={['click']}
    >
      <Button shape="circle" icon={<EllipsisOutlined />} />
    </Dropdown>
  );
};

export default EventActionMenu;
